// 家用面板語音設定 —— 家人端（V-24，docs/panel/09-voice-calendar.md §2.7、§2.8、§3.1、§8、§9.2、§9.8；VC2）。
// 這裡只放 API 與純邏輯，畫面在 PanelVoiceCard.tsx。只限主人。
//   - settings.voice：{on, wake_on, wake_word（預設「小幫手」，2–6 字）, lang（只有 cmn-Hant-TW）, cloud_enabled（VC4 才生效，先存）,
//     share_miss_text, phrases[{text ≤ 30, action, params}] ≤ 50}；PUT 沒給的欄位不改、phrases 整組取代。
//   - 喚醒詞（wake_on / wake_word）VC3 才生效、聽不懂時用網路幫忙（cloud_enabled）VC4 才生效：先存起來，畫面寫「之後的版本才會用到」。
//   - 422 INVALID_SETTING 的 details.reason → 文案（unsupported_lang / wake_word_length / reserved_phrase / wake_word_conflict /
//     duplicate_phrase / action_not_allowed / invalid_params）。
//   - 聽不懂統計 GET voice/misses?days=1–7 → {days, total, by_category, items?}（items 只有 share_miss_text 開著才有原文）。
//   - 教它：說法 + 動作（§3.1 表；門 / D2 拿掉的不在）+ 參數。要填日期 / 標題的行事曆動作（calendar.add / edit / delete）一句固定說法
//     沒有意義，教它不列。
import { ApiError, apiFetch } from '../../lib/api';

export const VOICE_DEFAULT_WAKE = '小幫手';
export const VOICE_DEFAULT_LANG = 'cmn-Hant-TW';
export const VOICE_PHRASES_MAX = 50;
export const VOICE_PHRASE_MAX_LEN = 30;
export const WAKE_MIN = 2;
export const WAKE_MAX = 6;

/** 語言下拉：只有國語能選，其他灰色「之後」（§2.4） */
export const VOICE_LANGS: { code: string; key: string; ready: boolean }[] = [
  { code: 'cmn-Hant-TW', key: 'panelVoice.lang.cmn', ready: true },
  { code: 'nan-TW', key: 'panelVoice.lang.nan', ready: false },
  { code: 'yue-Hant-HK', key: 'panelVoice.lang.yue', ready: false },
  { code: 'en-US', key: 'panelVoice.lang.en', ready: false },
];

export interface VoicePhrase {
  text: string;
  action: string;
  params: Record<string, unknown>;
}

export interface VoiceSettings {
  on: boolean;
  wakeOn: boolean;
  wakeWord: string;
  lang: string;
  cloudEnabled: boolean;
  shareMissText: boolean;
  phrases: VoicePhrase[];
}

const bool = (v: unknown, d: boolean) => (typeof v === 'boolean' ? v : d);
const str = (v: unknown) => (typeof v === 'string' ? v : '');

/** GET settings 的 voice（雲端已補預設；舊雲端沒有 voice 也照預設） */
export function parseVoiceSettings(raw: Record<string, unknown> | null | undefined): VoiceSettings {
  const v = (raw?.voice && typeof raw.voice === 'object' ? raw.voice : {}) as Record<string, unknown>;
  const phrases = Array.isArray(v.phrases)
    ? v.phrases.flatMap((p): VoicePhrase[] => {
      const o = (p && typeof p === 'object' ? p : {}) as Record<string, unknown>;
      if (typeof o.text !== 'string' || typeof o.action !== 'string') return [];
      return [{ text: o.text, action: o.action, params: o.params && typeof o.params === 'object' ? (o.params as Record<string, unknown>) : {} }];
    })
    : [];
  return {
    on: bool(v.on, true), wakeOn: bool(v.wake_on, true), wakeWord: str(v.wake_word) || VOICE_DEFAULT_WAKE, lang: str(v.lang) || VOICE_DEFAULT_LANG,
    cloudEnabled: bool(v.cloud_enabled, true), shareMissText: bool(v.share_miss_text, false), phrases,
  };
}

/** PUT settings 的 body：{voice: {只放改了的欄位}}；phrases 有改才整組送 */
export function voicePatch(before: VoiceSettings, after: VoiceSettings): Record<string, unknown> | null {
  const v: Record<string, unknown> = {};
  if (before.on !== after.on) v.on = after.on;
  if (before.wakeOn !== after.wakeOn) v.wake_on = after.wakeOn;
  if (before.wakeWord.trim() !== after.wakeWord.trim()) v.wake_word = after.wakeWord.trim();
  if (before.lang !== after.lang) v.lang = after.lang;
  if (before.cloudEnabled !== after.cloudEnabled) v.cloud_enabled = after.cloudEnabled;
  if (before.shareMissText !== after.shareMissText) v.share_miss_text = after.shareMissText;
  if (JSON.stringify(before.phrases) !== JSON.stringify(after.phrases)) v.phrases = after.phrases;
  return Object.keys(v).length ? { voice: v } : null;
}

export async function putVoiceSettings(panelId: string, body: Record<string, unknown>): Promise<VoiceSettings> {
  return parseVoiceSettings((await apiFetch<Record<string, unknown>>(`/v3/panels/${encodeURIComponent(panelId)}/settings`, { method: 'PUT', body })).data);
}

// ── 客戶端先擋（其餘以雲端 422 為準） ──

/** 正規化：去空白 / 標點 / 符號、轉小寫（同雲端 calNormTitle 的意思） */
export function normPhrase(s: string): string {
  return s.toLowerCase().replace(/[\s\p{P}\p{S}]+/gu, '');
}

/** 保留詞（§8；同雲端 panelVoiceReserved）：確認 / 取消、求救取消、提醒確認 */
export const VOICE_RESERVED = ['好', '是', '對', '嗯', '可以', '加入', '刪掉', '改', '不要', '不是', '算了', '取消',
  '不用了', '誤按了', '知道了', '吃過了', '吃了', '好了', '做好了', '等一下', '還沒', '有', '晚點再說'];

export type VoiceReason = 'unsupported_lang' | 'wake_word_length' | 'reserved_phrase' | 'wake_word_conflict' | 'duplicate_phrase'
  | 'action_not_allowed' | 'invalid_params' | 'phrase_length' | 'too_many';

export function wakeWordError(w: string, phrases: VoicePhrase[]): VoiceReason | null {
  const t = w.trim();
  const n = [...t].length;
  if (n < WAKE_MIN || n > WAKE_MAX) return 'wake_word_length';
  if (VOICE_RESERVED.includes(normPhrase(t))) return 'reserved_phrase';
  if (phrases.some((p) => normPhrase(p.text) === normPhrase(t))) return 'wake_word_conflict';
  return null;
}

/** 新加一句前先擋：長度、保留詞 / 喚醒詞、重複、筆數、參數 */
export function phraseError(p: VoicePhrase, existing: VoicePhrase[], wakeWord: string): VoiceReason | null {
  const t = p.text.trim();
  const norm = normPhrase(t);
  if (!norm || [...t].length > VOICE_PHRASE_MAX_LEN) return 'phrase_length';
  if (VOICE_RESERVED.includes(norm) || norm === normPhrase(wakeWord) || norm === normPhrase(VOICE_DEFAULT_WAKE)) return 'reserved_phrase';
  if (existing.some((x) => normPhrase(x.text) === norm)) return 'duplicate_phrase';
  if (existing.length >= VOICE_PHRASES_MAX) return 'too_many';
  if (!TEACH_ACTIONS.some((a) => a.action === p.action)) return 'action_not_allowed';
  if (!paramsValid(p.action, p.params)) return 'invalid_params';
  return null;
}

/** 422 INVALID_SETTING 的 details.reason → 文案 key；其他錯誤 → 一般 */
export function voiceErrKey(e: unknown): string {
  if (e instanceof ApiError) {
    const r = e.details?.reason;
    if (e.code === 'INVALID_SETTING' && typeof r === 'string' && VOICE_REASON_KEYS.includes(r as VoiceReason)) return `panelVoice.err.${r}`;
    if (e.code === 'INVALID_SETTING') return 'panelVoice.err.invalid';
    if (e.status === 403) return 'panelVoice.err.forbidden';
    return 'panelVoice.err.generic';
  }
  return 'panelVoice.err.network';
}
const VOICE_REASON_KEYS: VoiceReason[] = ['unsupported_lang', 'wake_word_length', 'reserved_phrase', 'wake_word_conflict', 'duplicate_phrase',
  'action_not_allowed', 'invalid_params', 'phrase_length', 'too_many'];

// ── 教它：動作與參數（§3.1；雲端 panelVoiceActions 同一份） ──

export type TeachField =
  | { name: string; kind: 'enum'; options: string[]; required: boolean }
  | { name: string; kind: 'text'; max: number; required: boolean }
  | { name: string; kind: 'bool'; required: boolean }
  | { name: string; kind: 'int'; min: number; max: number; required: boolean };

export interface TeachAction {
  action: string;
  fields: TeachField[];
}

export const TEACH_ACTIONS: TeachAction[] = [
  { action: 'date.today', fields: [{ name: 'what', kind: 'enum', options: ['date', 'weekday', 'time', 'lunar'], required: true }] },
  { action: 'calendar.query', fields: [{ name: 'range', kind: 'enum', options: ['today', 'tomorrow', 'day_after', 'this_week', 'next_week', 'next'], required: true },
    { name: 'keyword', kind: 'text', max: 20, required: false }, { name: 'for_whom', kind: 'text', max: 10, required: false }] },
  { action: 'calendar.view', fields: [{ name: 'view', kind: 'enum', options: ['month', 'week', 'day'], required: true }] },
  { action: 'reminder.ack', fields: [{ name: 'result', kind: 'enum', options: ['done', 'seen'], required: true }] },
  { action: 'reminder.snooze', fields: [{ name: 'minutes', kind: 'int', min: 5, max: 30, required: false }] },
  { action: 'reminder.query', fields: [{ name: 'range', kind: 'enum', options: ['today', 'yesterday'], required: false },
    { name: 'keyword', kind: 'text', max: 20, required: false }, { name: 'for_whom', kind: 'text', max: 10, required: false }] },
  { action: 'call.family', fields: [{ name: 'who', kind: 'text', max: 10, required: true }] },
  { action: 'sos.start', fields: [] },
  { action: 'camera.open', fields: [{ name: 'camera', kind: 'text', max: 20, required: true }] },
  { action: 'switch.set', fields: [{ name: 'device', kind: 'text', max: 20, required: true }, { name: 'on', kind: 'bool', required: true }] },
  { action: 'photos.show', fields: [] },
  { action: 'panel.volume', fields: [{ name: 'dir', kind: 'enum', options: ['up', 'down', 'min', 'max'], required: true }] },
  { action: 'panel.brightness', fields: [{ name: 'dir', kind: 'enum', options: ['up', 'down'], required: true }] },
  { action: 'panel.rest', fields: [] },
  { action: 'help', fields: [] },
  { action: 'voice.repeat', fields: [] },
  { action: 'voice.cancel', fields: [] },
];

export function teachAction(action: string): TeachAction | undefined {
  return TEACH_ACTIONS.find((a) => a.action === action);
}

/** 選了動作 → 預設參數（必填的 enum 取第一個、bool 取 true） */
export function defaultParams(action: string): Record<string, unknown> {
  const a = teachAction(action);
  const out: Record<string, unknown> = {};
  for (const f of a?.fields ?? []) {
    if (!f.required) continue;
    if (f.kind === 'enum') out[f.name] = f.options[0];
    else if (f.kind === 'bool') out[f.name] = true;
    else if (f.kind === 'text') out[f.name] = '';
  }
  return out;
}

/** 參數照 schema 清乾淨：空字串 / 不在表上的拿掉 */
export function cleanParams(action: string, params: Record<string, unknown>): Record<string, unknown> {
  const a = teachAction(action);
  const out: Record<string, unknown> = {};
  for (const f of a?.fields ?? []) {
    const v = params[f.name];
    if (v === undefined || v === null) continue;
    if (f.kind === 'text') {
      const t = String(v).trim();
      if (t) out[f.name] = t;
    } else if (f.kind === 'int') {
      if (v !== '' && Number.isFinite(Number(v))) out[f.name] = Number(v);
    } else if (v !== '') out[f.name] = v;
    else out[f.name] = v;
  }
  return out;
}

export function paramsValid(action: string, params: Record<string, unknown>): boolean {
  const a = teachAction(action);
  if (!a) return false;
  for (const f of a.fields) {
    const v = params[f.name];
    if (v === undefined || v === '') {
      if (f.required) return false;
      continue;
    }
    switch (f.kind) {
      case 'enum':
        if (!f.options.includes(String(v))) return false;
        break;
      case 'text': {
        const n = [...String(v).trim()].length;
        if (n < 1 || n > f.max) return false;
        break;
      }
      case 'bool':
        if (typeof v !== 'boolean') return false;
        break;
      case 'int':
        if (!Number.isInteger(v) || (v as number) < f.min || (v as number) > f.max) return false;
        break;
    }
  }
  return true;
}

// ── 聽不懂統計（GET voice/misses） ──

export const MISS_CATEGORIES = ['calendar', 'call', 'device', 'chat', 'noise', 'other'] as const;

export interface VoiceMisses {
  days: number;
  total: number;
  byCategory: Record<string, number>;
  items: { at: string; category: string; text: string }[];
}

export function parseVoiceMisses(raw: unknown): VoiceMisses {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const bc = (o.by_category && typeof o.by_category === 'object' ? o.by_category : {}) as Record<string, unknown>;
  const byCategory: Record<string, number> = {};
  for (const c of MISS_CATEGORIES) byCategory[c] = typeof bc[c] === 'number' ? (bc[c] as number) : 0;
  const items = Array.isArray(o.items)
    ? o.items.flatMap((x) => {
      const r = (x && typeof x === 'object' ? x : {}) as Record<string, unknown>;
      return typeof r.text === 'string' && r.text ? [{ at: str(r.at), category: str(r.category) || 'other', text: r.text }] : [];
    }).sort((a, b) => b.at.localeCompare(a.at))
    : [];
  return { days: typeof o.days === 'number' ? o.days : 7, total: typeof o.total === 'number' ? o.total : 0, byCategory, items };
}

export async function getVoiceMisses(panelId: string, days = 7): Promise<VoiceMisses> {
  return parseVoiceMisses((await apiFetch<unknown>(`/v3/panels/${encodeURIComponent(panelId)}/voice/misses`, { query: { days: String(days) } })).data);
}

/** 「行事曆 5、打電話 3、其他 4」：有次數的分類，多到少（同次數照表上的順序）；chat / noise / other 併成「其他」 */
export function missBreakdown(m: VoiceMisses): { cat: string; n: number }[] {
  const merged: Record<string, number> = { calendar: m.byCategory.calendar ?? 0, call: m.byCategory.call ?? 0, device: m.byCategory.device ?? 0,
    other: (m.byCategory.chat ?? 0) + (m.byCategory.noise ?? 0) + (m.byCategory.other ?? 0) };
  return Object.entries(merged).filter(([, n]) => n > 0).map(([cat, n]) => ({ cat, n }))
    .sort((a, b) => b.n - a.n || ORDER.indexOf(a.cat) - ORDER.indexOf(b.cat));
}
const ORDER = ['calendar', 'call', 'device', 'other'];
